import { debounce } from "lodash"
import { Controller } from "@hotwired/stimulus"

/**
 * Mixin for detecting when a controller element's layout changes via ResizeObserver.
 * Note that you can use this mixin only for Stimulus controllers.
 *
 * Usage:
 * import {useStableLayout} from "./mixins/use_stable_layout.js";
 *
 * export default class extends Controller {
 *   connect() {
 *     useStableLayout(this, { callback: 'onResizeChange' });
 *   }
 *
 *   onLayoutResizeChange() {
 *     // callback function
 *   }
 * }
 *
 * The functionality can be paused by this._pauseStableLayout() and
 * resumed by this._resumeStableLayout()
 */
export function useStableLayout(controller, options = {}) {
  const { debounceDelay = 100, callback = "onLayoutResizeChange" } = options

  if (!(controller instanceof Controller)) {
    console.error("useStableLayout: param controller is not instance of Stimulus Controller")
    throw new Error("useStableLayout: param controller is not instance of Stimulus Controller")
  }

  if (typeof controller[callback] !== "function") {
    console.error(`useStableLayout: controller.${callback} method is required`)
    throw new Error(`useStableLayout: controller.${callback} method is required`)
  }

  /**
   * Watch controller element resize changes using ResizeObserver.
   * Invoke callback function when element dimensions change.
   */
  const debouncedCheckLayoutChange = debounce(() => {
    if (controller._stableLayoutPaused) return
    controller[callback]()
  }, debounceDelay)

  controller._stableLayoutPaused = false
  controller._stableLayoutListener = debouncedCheckLayoutChange

  const resizeObserver = new ResizeObserver(controller._stableLayoutListener)
  resizeObserver.observe(controller.element)
  controller._stableLayoutObserver = resizeObserver

  const originalDisconnect = controller.disconnect.bind(controller)
  controller.disconnect = function () {
    if (controller._stableLayoutObserver) {
      controller._stableLayoutObserver.disconnect()
      controller._stableLayoutObserver = null
    }
    originalDisconnect()
  }
  controller._pauseStableLayout = () => {
    controller._stableLayoutPaused = true
  }
  controller._resumeStableLayout = () => {
    controller._stableLayoutPaused = false
  }
}
